import { Link } from 'react-router-dom'
import type { WorkflowConfig, WorkflowStepConfig } from '@kfolks/types'
import { Badge } from '@/shared/components/atoms/ui/badge'
import type { ApprovalDTO } from '../../api/request-center.api'

/**
 * Merged workflow timeline — overlays persisted approval rows on top of the
 * request type's workflow config so every step is rendered in authoring
 * order with a live status + deep link to its approval page (when the
 * engine has created one).
 *
 * Used by:
 *   - `RequestDetailPage` — `currentApprovalId={null}`; "active" is the
 *     first pending row the engine is sitting on.
 *   - `ApprovalDetailPage` — passes its own approval id as `currentApprovalId`
 *     to mark the one the user is currently viewing.
 *
 * Styling mirrors `@/components/WorkflowTimeline` (dot + connector line)
 * with status-coded colors. Keep this component dumb — callers own the
 * data fetching (workflow config + persisted rows).
 */

export type MergedStepStatus = 'done' | 'active' | 'rejected' | 'pending' | 'skipped' | 'not_started'

export interface MergedStep {
	stepOrder: number
	stepId: string
	label: string
	type: WorkflowStepConfig['type'] | null
	status: MergedStepStatus
	approval: ApprovalDTO | null
	isCurrent: boolean
}

export interface RequestWorkflowTimelineProps {
	workflowConfig: WorkflowConfig | null
	persistedRows: ApprovalDTO[]
	/** When set, this approval id is flagged "viewing" on its row. */
	currentApprovalId?: string | null
	/** Fallback step count when no workflow config is available (e.g. the
	 *  request type was deleted post-submit). Defaults to `persistedRows.length`. */
	totalStepsFallback?: number
}

const STATUS_DOT: Record<MergedStepStatus, string> = {
	done: 'bg-green-600',
	active: 'bg-blue-500 ring-4 ring-blue-200',
	rejected: 'bg-rose-500',
	pending: 'bg-amber-400',
	skipped: 'bg-muted',
	not_started: 'bg-muted',
}

const STATUS_LABEL: Record<MergedStepStatus, string> = {
	done: 'approved',
	active: 'pending',
	rejected: 'rejected',
	pending: 'pending',
	skipped: 'skipped',
	not_started: 'not started',
}

const STEP_TYPE_LABEL: Record<WorkflowStepConfig['type'], string> = {
	approval: 'Approval',
	info_gate: 'Info gate',
}

export function RequestWorkflowTimeline({
	workflowConfig,
	persistedRows,
	currentApprovalId = null,
	totalStepsFallback,
}: RequestWorkflowTimelineProps) {
	const merged = buildMergedTimeline(workflowConfig, persistedRows, currentApprovalId, totalStepsFallback)
	if (merged.length === 0) {
		return <p className="text-sm text-muted-foreground">No workflow steps to display.</p>
	}
	return (
		<ol className="relative space-y-4" data-testid="request-workflow-timeline">
			<div aria-hidden className="absolute bottom-2 left-[5px] top-2 w-px bg-muted" />
			{merged.map((step) => (
				<Row key={step.stepOrder} step={step} />
			))}
		</ol>
	)
}

function Row({ step }: { step: MergedStep }) {
	const a = step.approval
	const decidedAt = a?.decidedAt ? new Date(a.decidedAt).toLocaleString() : null
	const actor = a ? (a.approverDisplayNames[0] ?? a.resolvedManagerDisplayName ?? null) : null
	const labelNode = <span className={`text-sm font-medium ${step.isCurrent ? 'text-primary' : ''}`}>{step.label}</span>
	const heading = a ? (
		<Link className="underline-offset-2 hover:underline" to={`/requests-center/approvals/${a.id}`}>
			{labelNode}
		</Link>
	) : (
		labelNode
	)
	return (
		<li
			className="relative flex items-start gap-3 pl-6"
			data-step-id={step.stepId}
			data-status={step.status}
			data-testid={step.isCurrent ? 'request-timeline-current' : undefined}
		>
			<span aria-hidden className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ${STATUS_DOT[step.status]}`} />
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-xs text-muted-foreground">Step {step.stepOrder}</span>
					{heading}
					<Badge variant="outline">{STATUS_LABEL[step.status]}</Badge>
					{step.type ? <span className="text-xs text-muted-foreground">· {STEP_TYPE_LABEL[step.type]}</span> : null}
					{step.isCurrent ? <Badge>viewing</Badge> : null}
				</div>
				<p className="mt-0.5 text-xs text-muted-foreground">{renderSubtitle(step, { decidedAt, actor })}</p>
				{a?.comment ? <p className="mt-0.5 text-xs italic text-muted-foreground">“{a.comment}”</p> : null}
			</div>
		</li>
	)
}

function renderSubtitle(step: MergedStep, ctx: { decidedAt: string | null; actor: string | null }): string {
	const { decidedAt, actor } = ctx
	switch (step.status) {
		case 'done':
			return `Approved${decidedAt ? ` on ${decidedAt}` : ''}${actor ? ` · by ${actor}` : ''}`
		case 'rejected':
			return `Rejected${decidedAt ? ` on ${decidedAt}` : ''}${actor ? ` · by ${actor}` : ''}`
		case 'active': {
			const due = step.approval?.dueAt ? new Date(step.approval.dueAt).toLocaleString() : null
			return `Pending${actor ? ` — ${actor}` : ''}${due ? ` · due ${due}` : ''}`
		}
		case 'pending':
			return 'Pending.'
		case 'skipped':
			return `Skipped${actor ? ` — ${actor}` : ''}`
		case 'not_started':
			return 'Opens once the previous step is approved.'
	}
}

/**
 * Merge the workflow config (labels + step ids for every step) with the
 * persisted approval rows (statuses, actors, timestamps). Config wins for
 * ordering; persisted rows attach where their `stepName` matches a step id.
 *
 * `currentApprovalId` marks a specific row as "viewing". When it's null the
 * first `pending` row (if any) is still styled as `active` so the viewer can
 * see which step the engine is sitting on.
 *
 * Fallback when config is still loading (or the request type was deleted
 * after submit): fall back to the persisted rows alone, padded out to
 * `totalStepsFallback` so the viewer still sees the partition count.
 */
function buildMergedTimeline(
	workflowConfig: WorkflowConfig | null,
	persistedRows: ApprovalDTO[],
	currentApprovalId: string | null,
	totalStepsFallback: number | undefined,
): MergedStep[] {
	const rowsByStepId = new Map(persistedRows.map((r) => [r.stepName, r]))
	const firstPending = persistedRows.find((r) => r.status === 'pending')
	const activeApprovalId = currentApprovalId ?? firstPending?.id ?? null

	if (workflowConfig && workflowConfig.steps?.length) {
		return workflowConfig.steps.map((cfgStep, idx) => {
			const approval = rowsByStepId.get(cfgStep.id) ?? null
			return {
				stepOrder: idx + 1,
				stepId: cfgStep.id,
				label: cfgStep.label,
				type: cfgStep.type,
				status: resolveStatus(approval, activeApprovalId),
				approval,
				isCurrent: approval?.id === currentApprovalId,
			}
		})
	}

	const total = Math.max(totalStepsFallback ?? 0, persistedRows.length)
	return Array.from({ length: total }, (_, idx): MergedStep => {
		const approval = persistedRows.find((r) => r.stepOrder === idx + 1) ?? null
		return {
			stepOrder: idx + 1,
			stepId: approval?.stepName ?? `step-${idx + 1}`,
			label: approval?.stepLabel ?? approval?.stepName ?? `Step ${idx + 1}`,
			type: null,
			status: resolveStatus(approval, activeApprovalId),
			approval,
			isCurrent: approval?.id === currentApprovalId,
		}
	})
}

function resolveStatus(approval: ApprovalDTO | null, activeApprovalId: string | null): MergedStepStatus {
	if (!approval) return 'not_started'
	if (approval.status === 'approved') return 'done'
	if (approval.status === 'rejected') return 'rejected'
	if (approval.status === 'skipped') return 'skipped'
	// pending — the "active" row (the one the engine is on / the viewer is on)
	// gets the primary treatment; other pending rows stay amber.
	return approval.id === activeApprovalId ? 'active' : 'pending'
}
